import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, Pagination } from "swiper/modules";
import { useNavigate, Link } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { ChevronLeft, ChevronRight, Calendar, User, ArrowRight } from "lucide-react";
import BlogManager from "../../../services/dashboard/BlogManager";
import { getBlogImageUrl } from "../../../utils/mediaUrls";

export default function BlogSection() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchActiveBlogs();
  }, []);

  const fetchActiveBlogs = async () => {
    try {
      const data = await BlogManager.getActiveBlogs();
      setBlogs(data || []);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setBlogs([]);
      } else {
        console.error("Failed to fetch active blogs:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateArray) => {
    if (!dateArray) return "-";
    if (Array.isArray(dateArray)) {
      const date = new Date(dateArray[0], dateArray[1] - 1, dateArray[2]);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }
    return new Date(dateArray).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || "").replace(/&nbsp;/g, ' ').trim();
  };

  if (loading) {
    return (
      <section className="py-16 md:py-24 relative overflow-hidden bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        </div>
      </section>
    );
  }

  if (!blogs || blogs.length === 0) {
    return null; // Don't show the section if there are no active blogs
  }

  return (
    <section className="py-16 md:py-12 relative overflow-hidden bg-white dark:bg-gray-900">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px]  rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/3"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full blur-3xl opacity-50 translate-y-1/3 -translate-x-1/3"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 max-w-7xl mx-auto gap-4">
          <div className="text-left">
            <h2 className="text-2xl md:text-3xl font-bold text-text-muted dark:text-white w-fit border-b-2 border-primary pb-1">
              Latest News & Articles
            </h2>
          </div>
          <Link 
            to="/blog"
            className="flex items-center gap-1 px-1.5 py-2 bg-white dark:bg-gray-800 border-2 border-orange-500 text-orange-500 font-semibold rounded-full hover:bg-orange-500 hover:text-white transition-all shadow-sm shrink-0"
          >
            View All Articles
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>

        <div className="relative group">
          <Swiper
          
            modules={[Navigation, Autoplay, Pagination]}
            spaceBetween={30}
            slidesPerView={1}
            navigation={{
              nextEl: '.blog-swiper-button-next',
              prevEl: '.blog-swiper-button-prev',
            }}
            pagination={{ clickable: true, el: '.blog-swiper-pagination' }}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            breakpoints={{
              640: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            className="pb-16"
          >
            {blogs.map((blog) => (
              <SwiperSlide key={blog.blogId} className="h-auto">
                <div 
                  className="h-full bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-100  flex flex-col cursor-pointer transition-shadow hover:shadow-lg group/card"
                  onClick={() => navigate(`/blog/${blog.blogId}`)}
                >
                  {/* Blog Image */}
                  <div className="relative h-56 bg-gray-100 dark:bg-gray-800 flex justify-center items-center overflow-hidden">
                    <img
                      src={getBlogImageUrl(blog.imageUrl) || "/images/defaultBook.png"}
                      alt={blog.title}
                      className="w-full h-full object-fit object-top"
                      onError={(e) => { e.target.src = "/images/defaultBook.png" }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center text-white text-sm">
                      <div className="flex items-center gap-1.5 backdrop-blur-sm bg-white/20 px-3 py-1 rounded-full">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{formatDate(blog.creationDate)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Blog Content */}
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 hover:text-orange-500 transition-colors">
                      {blog.title}
                    </h3>
                    
                    <p className="text-gray-600 dark:text-gray-400 mb-6 line-clamp-3 flex-1">
                      {stripHtml(blog.excerpt || blog.content)}
                    </p>

                    <div className="mt-auto pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400">
                          <User className="w-4 h-4" />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {blog.author || "Admin"}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-1 text-sm font-semibold text-orange-500 hover:text-orange-600 transition-colors group/btn">
                        Read More
                        <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom Navigation */}
          <button className="blog-swiper-button-prev absolute top-1/2 -left-4 md:-left-6 lg:-left-12 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white dark:bg-gray-800 shadow-xl border border-gray-100 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-orange-500 dark:hover:text-orange-400 hover:border-orange-500 transition-all opacity-0 group-hover:opacity-100">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button className="blog-swiper-button-next absolute top-1/2 -right-4 md:-right-6 lg:-right-12 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white dark:bg-gray-800 shadow-xl border border-gray-100 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-orange-500 dark:hover:text-orange-400 hover:border-orange-500 transition-all opacity-0 group-hover:opacity-100">
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Custom Pagination */}
          <div className="blog-swiper-pagination flex justify-center gap-2 mt-4"></div>
        </div>
      </div>
    </section>
  );
}
